import { APP_INITIALIZER, Injector, NgModule, inject } from '@angular/core';

import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MAT_TABS_CONFIG, MatTabsConfig } from '@angular/material/tabs';

import {
  FS_API_PRE_RESPONSE_INTERCEPTOR,
  FS_API_REQUEST_INTERCEPTOR,
  FsApi,
  FsApiModule,
} from '@firestitch/api';
import { FS_APP_ACL_CONFIG } from '@firestitch/app-acl';
import { FS_APP_MESSAGE_CONFIG } from '@firestitch/app-message';
import { FS_ATTRIBUTE_CONFIG, FsAttributeModule } from '@firestitch/attribute';
import { FsAutocompleteChipsModule } from '@firestitch/autocomplete-chips';
import { FS_BANNER_CONFIG } from '@firestitch/banner';
import {
  BuildConfig,
  FS_BUILD_CONFIG,
  FsBuildService,
  UpdateAction,
} from '@firestitch/build';
import { FsConnectionModule } from '@firestitch/connection';
import { FS_CONTENT_CONFIG } from '@firestitch/content';
import { FS_CONTENT_WIDGET_CONFIG } from '@firestitch/content-widget';
import { FsDatePickerModule } from '@firestitch/datepicker';
import { FsDialogModule } from '@firestitch/dialog';
import { DRAWER_DEFAULT_CONFIG } from '@firestitch/drawer';
import { FsFileModule } from '@firestitch/file';
import { ButtonStyle, FsFilterModule } from '@firestitch/filter';
import { FsFontPickerModule } from '@firestitch/font-picker';
import { FsFormModule } from '@firestitch/form';
import { FsGalleryModule } from '@firestitch/gallery';
import { FsBodyModule } from '@firestitch/html';
import { FsHtmlEditorModule } from '@firestitch/html-editor';
import { FsListModule } from '@firestitch/list';
import { FS_MAP_GOOGLE_MAP_KEY } from '@firestitch/map';
import { FsMessage, FsMessageModule } from '@firestitch/message';
import { FsNavModule } from '@firestitch/nav';
import { FS_OFFLINE_CONFIG } from '@firestitch/offline';
import { FsPasswordModule } from '@firestitch/password';
import { FsPhoneModule } from '@firestitch/phone';
import { FsProgressModule } from '@firestitch/progress';
import { FsPrompt } from '@firestitch/prompt';
import { FsScrollModule } from '@firestitch/scroll';
import { FsScrollbarModule } from '@firestitch/scrollbar';
import { FsSelectionModule } from '@firestitch/selection';
import { FsSigninModule, SigninConfig } from '@firestitch/signin';
import { FsSigninSecurityModule } from '@firestitch/signin-security';
import {
  FacebookSigninProvider,
  FsSocialSignin,
  GoogleSigninProvider,
} from '@firestitch/social-signin';
import { FsStore, FsStoreModule } from '@firestitch/store';
import { FsTabsModule } from '@firestitch/tabs';
import { FsTextEditorModule } from '@firestitch/text-editor';
import { FsUploadModule } from '@firestitch/upload';

import { of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AclRoleLevels, ListDefaultConfig } from '@common/consts';
import {
  AclRoleData,
  AttributeData,
  BannerData, CmsData,
  MessageData, MessageQueueData,
  MessageTemplateData, MessageWebhookData,
  OfflineData,
} from '@common/data';
import {
  attributeConfigFactory,
  bannerConfig,
  contentConfigFactory,
  contentWidgetConfigFactory,
  offlineConfig,
  throwIfAlreadyLoaded,
} from '@common/helpers';
import { ENVIRONMENT } from '@common/injectors';
import { ApiInterceptorFactory, PermissionInterceptorFactory } from '@common/interceptors';
import {
  AccountService, Acl, AppService,
  SessionService, SigninService,
} from '@common/services';
import { environment } from '@env/environment';

import { appMessageConfig } from './helpers/app-message-config';


@NgModule({
  imports: [
    FsPasswordModule.forRoot(),
    FsGalleryModule.forRoot(),
    FsTextEditorModule.forRoot(),
    FsFormModule.forRoot(),
    FsScrollModule.forRoot(),
    FsBodyModule.forRoot(),
    FsUploadModule.forRoot(),
    FsFileModule.forRoot(),
    FsTabsModule.forRoot(),
    FsAutocompleteChipsModule.forRoot(),
    FsAttributeModule.forRoot(),
    FsScrollbarModule.forRoot(),
    FsDatePickerModule.forRoot(),
    FsDialogModule.forRoot(),
    FsConnectionModule.forRoot(),
    FsProgressModule.forRoot(),
    FsApiModule.forRoot({
      maxFileConnections: 5,
    }),
    FsNavModule.forRoot({
      watchBrowserBackButton: false,
    }),
    FsListModule.forRoot(ListDefaultConfig),
    FsFilterModule.forRoot({
      case: 'camel',
      buttonStyle: ButtonStyle.Flat,
      queryParam: true,
      chips: true,
      button: {
        label: '',
      },
    }),
    FsSigninSecurityModule.forRoot({
      case: 'camel',
    }),
    FsPhoneModule.forRoot({
      isoCountryCode: 'CA',
    }),
    FsMessageModule.forRoot({
      defaultMessage: {
        success: 'Saved Changes',
      },
      positionClass: 'toast-top-right',
    }),
    FsFontPickerModule.forRoot({
      googleApiKey: 'AIzaSyDMnGc3CbikvvHkTYBw0QKtWRaQNwK1reE',
    }),
    FsHtmlEditorModule.forRoot({
      activationKey: '2J1B10dD7F6F5A3F3I3cWHNGGDTCWHId1Eb1Oc1Yh1b2Ld1POkE3D3F3C9A4E5A3G3B2G2==',
    }),
    FsSigninModule.forRoot({
      factory: (appService: AppService, signinService: SigninService): SigninConfig => {
        return {
          trustedDeviceExpiryDays: appService.settings.trustedDeviceExpiryDays,
          showSocialSignins: true,
          processSignin: (response, redirect: string) => signinService
            .processSignin(response, redirect),
          signinMeta: () => of({
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            appType: (navigator as any).standalone ? 'pwa' : 'web',
          }),
        } as SigninConfig;
      },
      deps: [AppService, SigninService],
    }),
    FsSelectionModule,
    FsStoreModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (
        appService: AppService,
        buildService: FsBuildService,
        socialSignin: FsSocialSignin,
        acl: Acl,
      ) => {
        return () => appService.load()
          .pipe(
            tap(() => {
              if (environment.configuration !== 'local') {
                buildService.listen();
              }
            }),
            tap(() => {
              acl.init();
            }),
            tap(() => {
              socialSignin.init({
                providers: [
                  {
                    provider: new GoogleSigninProvider(appService.settings.googleClientId),
                    enabled: appService.settings.googleSigninEnabled,
                  },
                  {
                    provider: new FacebookSigninProvider(appService.settings.facebookAppId),
                    enabled: appService.settings.facebookSigninEnabled,
                  },
                ]
                  .filter((item) => item.enabled)
                  .map((item) => item.provider),
              });
            }),
          );
      },
      multi: true,
      deps: [AppService, FsBuildService, FsSocialSignin, Acl],
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        maxWidth: '95vw',
        hasBackdrop: true,
        autoFocus: false,
        closeOnNavigation: true,
        minWidth: '450px',
      } as MatDialogConfig,
    },
    {
      provide: MAT_TABS_CONFIG,
      useValue: {
        animationDuration: '0ms',
        stretchTabs: false,
        dynamicHeight: false,
      } as MatTabsConfig,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { 
        appearance: 'fill', 
        floatLabel: 'auto', 
        hideRequiredMarker: false,        
      } as MatFormFieldDefaultOptions,
    },
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: FS_API_REQUEST_INTERCEPTOR,
      useFactory: ApiInterceptorFactory,
      deps: [Injector],
      multi: true,
    },
    {
      provide: FS_API_PRE_RESPONSE_INTERCEPTOR,
      useFactory: PermissionInterceptorFactory,
      deps: [Injector],
      multi: true,
    },
    {
      provide: FS_MAP_GOOGLE_MAP_KEY,
      useFactory: (appService: AppService) => appService.settings.googleMapKey,
      deps: [AppService],
    },
    {
      provide: FS_ATTRIBUTE_CONFIG,
      useFactory: attributeConfigFactory,
      deps: [
        AttributeData,
        FsPrompt,
        FsMessage,
      ],
    },
    {
      provide: FS_BUILD_CONFIG,
      useFactory: (): BuildConfig => {
        return {
          updateAction: environment.production ?
            UpdateAction.NavigationUpdate : UpdateAction.PromptUpdate,
        };
      },
    },
    {
      provide: DRAWER_DEFAULT_CONFIG,
      useValue: {
        width: {
          side: {
            min: 300,
          },
        },
      },
    },
    {
      provide: FS_APP_ACL_CONFIG,
      useFactory: (aclRoleData: AclRoleData) => {
        return {
          permissions: aclRoleData.permissions(),
          levels: of(AclRoleLevels),
        };
      },
      deps: [AclRoleData],
    },
    {
      provide: FS_APP_MESSAGE_CONFIG,
      useFactory: appMessageConfig,
      deps: [
        MessageData, MessageQueueData, MessageTemplateData,
        MessageWebhookData, AccountService, SessionService,
      ],
    },
    {
      provide: FS_OFFLINE_CONFIG,
      useFactory: offlineConfig,
      deps: [OfflineData, Acl],
    },
    {
      provide: FS_BANNER_CONFIG,
      useFactory: bannerConfig,
      deps: [BannerData],
    },
    {
      provide: FS_CONTENT_CONFIG,
      useFactory: contentConfigFactory,
      deps: [CmsData],
    },
    {
      provide: FS_CONTENT_WIDGET_CONFIG,
      useFactory: contentWidgetConfigFactory,
      deps: [CmsData],
    },
    FsApi,
    FsStore,
  ],
  exports: [
    FsProgressModule,
  ],
})
export class CoreModule {

  constructor() {
    const parentModule = inject(CoreModule, { optional: true, skipSelf: true });

    throwIfAlreadyLoaded(parentModule, 'CoreModule');
  }

}
